import { FormField, FormTextField } from "@avamae/formbuilder";
import Button from "components/ui/Button";
import { useField, useFormikContext } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { Worker, Viewer, ScrollMode } from "@react-pdf-viewer/core";
// Plugins
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
// import { ExcelRenderer } from "react-data-export";

// import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";

// import FileViewer from "react-file-viewer";
import Papa from "papaparse";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectTimeoutPrompt } from "reducers/auth";
import { Transition } from "react-transition-group";

import closeIcon from "assets/Icon_Btn_06.png";
import { customUseOnClickOutside } from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/SearchListRenderer";
import {
  ALL_FIELDS_KEY,
  AcceptedFileTypes,
  FileUpload,
  MAX_FILE_SIZE,
} from "components/helpers/FieldRenderers/FileUpload/fileUploadFieldRenderer";
import Dropzone from "react-dropzone";
import UploadIcon from "assets/Icon_Upload_Cloud.png";
import PDFViewer from "components/PdfViewer/PdfViewer";
import api from "api";
import { endpoints } from "config";
import { getFileTemplate } from "./useDownloader";
import { toast } from "react-toastify";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";

type Props = {
  readOnly: boolean;
  field: FormField<any>;
  module?: "SURVEYS";
  rightColumnViewer?: boolean;
  acceptedFileTypes?: AcceptedFileTypes;
  bHasUpdateDocumentPermissions?: boolean;
  newFileFieldName?: string;
};

type FileType = {
  fileName: string;
  data: File;
  preview: string;
  fileExtension: string;
};

export const CustomFileViewer: React.FC<Props> = ({
  field,
  readOnly,
  module,
  rightColumnViewer,
  acceptedFileTypes,
  bHasUpdateDocumentPermissions,
  newFileFieldName,
}) => {
  let finalAcceptedFileTypes: AcceptedFileTypes = { ...acceptedFileTypes };
  const dispatch = useDispatch();

  if (!acceptedFileTypes) {
    finalAcceptedFileTypes[ALL_FIELDS_KEY] = [
      "application/msword",
      "application/pdf",
      "image/jpg",
      "image/jpeg",
      "image/png",
    ]; // MIME File Types
  }

  const [{ value }, _, helpers] = useField<any>(field.name as string);
  const { values, setFieldValue, errors, setErrors } = useFormikContext<any>();
  const [uploadNewVersion, setUploadNewVersion] = useState<boolean>(false);
  const [newUploadToggle, setNewUploadToggle] = useState<boolean>(false); //optional
  const [file, setFile] = useState<FileType | null>(null);
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const fieldAcceptedFileTypes = finalAcceptedFileTypes.hasOwnProperty(String(field.name))
    ? finalAcceptedFileTypes[String(field.name)]
    : finalAcceptedFileTypes[ALL_FIELDS_KEY];
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [thumbnails, setThumbnails] = useState<any>(null);
  const newPlugin = defaultLayoutPlugin();
  const [parsedAcceptedFileTypes, setParsedAcceptedFileTypes] = useState<string | null>(null);
  const bind = customUseOnClickOutside(() => setUploadNewVersion(false), false);

  const iframeRef = useRef<any>(null);

  const toggleFullscreen = () => {
    const iframe = iframeRef.current;

    if (iframe) {
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      } else if (iframe.mozRequestFullScreen) {
        iframe.mozRequestFullScreen();
      } else if (iframe.webkitRequestFullscreen) {
        iframe.webkitRequestFullscreen();
      } else if (iframe.msRequestFullscreen) {
        iframe.msRequestFullscreen();
      }
    }
  };

  useEffect(() => {
    if (finalAcceptedFileTypes && finalAcceptedFileTypes.hasOwnProperty(String(field.name))) {
      let finalTypes: string[] = [];

      finalAcceptedFileTypes[String(field.name)].map((item) => {
        const parsedType = item.split("/");

        let finalType = item;
        if (parsedType.length > 1) {
          finalType = parsedType[parsedType.length - 1];

          finalTypes.push(`.${finalType}`);
        }
      });

      if (finalTypes.length > 0) {
        setParsedAcceptedFileTypes(finalTypes.join(", "));
      } else {
        setParsedAcceptedFileTypes(null);
      }
    } else {
      setParsedAcceptedFileTypes(null);
    }
  }, [finalAcceptedFileTypes]);

  const fileType = useMemo(() => {
    switch (values.fileExtension) {
      case ".PNG":
      case ".png":
      case ".JPEG":
      case ".jpeg":
      case ".GIF":
      case ".gif":
        return "Image";
      case ".MP4":
      case ".mp4":
        return "Video";
      case ".pdf":
      case ".PDF":
        return "PDF";
      case ".docx":
      case ".DOCX":
      case ".xlsx":
      case ".XLSX":
      case ".pptx":
      case ".PPTX":
        return "Office";
      case ".dwg":
      case ".DWG":
        return "CAD";
      default:
        return null;
    }
  }, [values.fileExtension]);

  const getFileViewer = (fileExtension: string, value: any) => {
    switch (fileExtension) {
      case ".PNG":
      case ".png":
      case ".jpg":
      case ".JPG":
      case ".JPEG":
      case ".jpeg":
      case ".GIF":
      case ".gif": {
        return (
          <div className="FileImageContainer">
            <img
              src={value}
              // Revoke data uri after image is loaded
              onLoad={() => {}}
              alt="File preview"
              className="DocFileImagePreview"
              onClick={() => {
                setFullScreen(true);
              }}
            />
          </div>
        );
      }
      case ".MP4":
      case ".mp4": {
        return (
          <video width={"100%"} controls className="video" autoPlay={false}>
            <source src={value} type="video/mp4" />
          </video>
        );
      }
      case ".pdf":
      case ".PDF": {
        return (
          <div
            style={{
              height: "750px",
            }}
          >
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer fileUrl={value} plugins={[newPlugin]} />
            </Worker>
          </div>
        );
      }
      case ".docx":
      case ".DOCX":
      case ".xlsx":
      case ".XLSX":
      case ".pptx":
      case ".PPTX": {
        return (
          <div style={{ width: "100%" }}>
            <iframe
              ref={iframeRef}
              title={"PDF-Viewer"}
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                value
              )}`}
              frameBorder={0}
              style={{ height: "100vh", width: "100%" }} //width: "90vw"
            ></iframe>
          </div>
        );
      }
      case ".csv":
      case ".CSV": {
        return <CsvDisplay csvFileUrl={value} />;
      }

      default:
        return null;
    }
  };

  useEffect(() => {
    if (file === null) {
      setThumbnails(null);
    } else if (file && file.fileName && file.preview) {
      const parsedFileName = file.fileName.split(".");
      if (parsedFileName[parsedFileName.length - 1] === "pdf") {
        setThumbnails(
          <PDFViewer
            file={file} //file[file.length-1] or field.value or field.value[field.value?.length-1]
            fileName={file.fileName}
            showTitle={false}
            overflowHidden={true}
          />
        );
      } else {
        setThumbnails(
          <div className="Thumbnails" key={file?.fileName}>
            <div className="ThumbnailsInner">
              <img
                src={file?.preview}
                className="ThumbnailsImage"
                // Revoke data uri after image is loaded
                onLoad={() => {
                  URL.revokeObjectURL(file?.preview);
                }}
                alt="Uploaded file preview"
              />
            </div>
          </div>
        );
      }
    }
  }, [file]);

  const clearFieldError = () => {
    let newErrors = { ...errors };

    if (newErrors.hasOwnProperty(field.name)) {
      delete newErrors[field.name as keyof typeof newErrors];
    }

    setErrors(newErrors);
    //alternatively just setFieldError(field.name, undefined)
  };

  const submitNewFile = async (file: File, id: number) => {
    try {
      const payload = {
        id,
        file,
      };
      const response = await api.post(endpoints.invoices.update_document, payload, {
        headers: { "Content-type": "multipart/file" },
      });

      if (
        response.status === 200 &&
        response.data.status === `1` &&
        response.data.details &&
        response.data.details.filePath
      ) {
        const fileName = file.name;

        const fileExtension = `.${fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2)}`;

        setFile({
          data: file,
          fileName,
          preview: URL.createObjectURL(file),
          fileExtension,
        }); //last element of files array (acceptedFiles)

        setFieldValue(`fileName`, fileName);
        setFieldValue(`fileExtension`, file.type);
        setFieldValue(`filePath`, response.data.details.filePath ?? null);
        // setFieldValue(`filePath`, file?.name ?? null);

        toast(
          <ToastMessage
            toastData={[
              {
                title: "Success",
                text: "Updated document",
              },
            ]}
            className="ToastError"
          />,
          {
            ...toastConfig,
          }
        );

        setUploadNewVersion(false);
      } else {
        toast(
          <ToastMessage
            toastData={[
              {
                title: "Error",
                text: "Failed to update document. Please try again later.",
              },
            ]}
            className="ToastError"
          />,
          {
            ...toastConfig,
          }
        );
      }
    } catch (err) {
      toast(
        <ToastMessage
          toastData={[
            {
              title: "Error",
              text: "Failed to update document. Please try again later.",
            },
          ]}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    clearFieldError();

    let fileName = "";
    const reader = new FileReader();

    reader.onabort = () => console.log("File reading was aborted");
    reader.onerror = () => console.log("File reading has failed");

    if (acceptedFiles.length > 1) {
      setTimeout(() => {}, 2500);
      return;
    }

    acceptedFiles.forEach((file) => {
      // METHOD 1:
      fileName = file.name;

      const fileExtension = `.${fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2)}`;

      setFile({
        data: file,
        fileName,
        preview: URL.createObjectURL(file),
        fileExtension,
      }); //last element of files array (acceptedFiles)

      setFieldValue(`fileName`, fileName);
      setFieldValue(`fileExtension`, file.type);
      setFieldValue(`filePath`, file?.name ?? null);
      setUploadNewVersion(false);
    });

    setNewUploadToggle(!newUploadToggle); //optional
  };

  useEffect(() => {
    if (file && file.data && newFileFieldName) {
      setFieldValue(newFileFieldName, file.data);
    }
  }, [newUploadToggle]); //optional

  const validateFile = (file: File) => {
    let errors: string[] = [];

    if (!fieldAcceptedFileTypes.includes(file.type)) {
      const messageCode = "PleaseSelectAValidFileType";
      errors = [...errors, messageCode];
    }
    if (MAX_FILE_SIZE && file.size > MAX_FILE_SIZE) {
      const messageCode = "TheSelectedFileExceedsTheMaximumFileSize";
      errors = [...errors, messageCode];
    }

    setFileErrors(errors);
  };

  if (module && module === "SURVEYS" && value === null) {
    return (
      <div className="FormField">
        <p>Survey not complete, pdf available when submitted</p>
      </div>
    );
  }

  return (
    <div className="CustomFileViewer">
      {getFileViewer(file?.fileExtension ?? values.fileExtension, file?.preview ?? value)}
      {fullScreen && (
        <CustomFullScreenFilerViewer
          fileExt={file?.fileExtension ?? values.fileExtension}
          open={fullScreen}
          closeModal={() => setFullScreen(false)}
          getFileViewer={getFileViewer}
          value={file?.preview ?? value}
        />
      )}

      {uploadNewVersion && (
        <div className="UploadNewVersionContainer" {...bind}>
          <div className="Text">Upload new invoice: </div>

          <img
            src={closeIcon}
            alt="close file preview"
            className="CloseIcon"
            onClick={() => setUploadNewVersion(false)}
          />

          <Dropzone
            onDrop={(acceptedFiles, rejectedFiles, event) => onDrop(acceptedFiles)}
            onDropRejected={(files) => validateFile(files[0])}
            onDropAccepted={() => setFileErrors([])}
            accept={fieldAcceptedFileTypes}
            maxSize={MAX_FILE_SIZE}
          >
            {({ getRootProps, getInputProps }) => (
              <div className={`DropzoneContainer`} {...getRootProps()}>
                <div className="Contents">
                  <img className={`UploadIcon`} src={UploadIcon} alt="Upload" />
                  <input {...getInputProps()} />

                  <h3>File</h3>
                  <span>Click or drag to upload</span>
                </div>
                {thumbnails && <div className="ThumbnailsContainer">{thumbnails}</div>}
              </div>
            )}
          </Dropzone>

          <div className="FileTypes">{`Accepts: ${parsedAcceptedFileTypes ?? "-"}`}</div>

          <div className="FileUploadErrors">
            {fileErrors &&
              fileErrors.map((item) => {
                return <div className="Error">{item}</div>;
              })}
          </div>
        </div>
      )}

      {rightColumnViewer && (
        <div className="HeaderButtons">
          {bHasUpdateDocumentPermissions && (
            <Button type="button" onClick={() => setUploadNewVersion(true)}>
              Update document
            </Button>
          )}

          <Button type="button" onClick={() => setFullScreen(true)}>
            Full size invoice
          </Button>
        </div>
      )}

      <div className="FileViewerButtons">
        {fileType === "Office" && (
          <Button type="button" onClick={toggleFullscreen}>
            Full screen
          </Button>
        )}
        {!rightColumnViewer && values.fileExtension && values.fileName && value && (
          <Button
            type="button"
            onClick={() => {
              console.log("values", values);

              const parsedData =
                values && values.versionNumber && values.id
                  ? [
                      {
                        id: values.id,
                        versionNumber: values.versionNumber,
                      },
                    ]
                  : null;

              let documentName =
                values && values.fileName
                  ? values.fileName
                  : values && values.id
                    ? `Document ${values.id}`
                    : "Document";

              if (values && values.fileExtension) {
                documentName += values.fileExtension;
              }

              console.log("documentName: ", documentName);

              if (parsedData) {
                getFileTemplate(
                  endpoints.documents.download,
                  parsedData,
                  false, // Only ever one file being downloaded using this button
                  dispatch,
                  documentName,
                  undefined,
                  undefined
                );
              }
            }}
          >
            Download
          </Button>
        )}
      </div>
    </div>
  );
};

const CustomFullScreenFilerViewer = ({
  fileExt,
  open,
  closeModal,
  getFileViewer,
  value,
}: {
  fileExt: string;
  open: boolean;
  closeModal(): void;
  getFileViewer?: (fileExtension: string, value: any) => JSX.Element | null;
  value: any;
}) => {
  const [delayedOpen, setDelayedOpen] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setTimeout(() => {
        setDelayedOpen(true);
      }, 1);
    }
  }, [open]);
  const timeoutPrompt = useSelector(selectTimeoutPrompt);
  const bind = customUseOnClickOutside(!timeoutPrompt ? closeModal : () => {}, true);

  return (
    <Transition in={delayedOpen} timeout={100} mountOnEnter unmountOnExit>
      {(state) => (
        <div className={"ModalUnderlay FileViewer " + state}>
          {/* <div className={"Modal  "} {...bind}> */}
          <div className={"Modal FullSize FileViewer"} {...bind}>
            <div className="CloseIcon" style={{ float: "right" }}>
              <img src={closeIcon} alt="close file preview" onClick={() => closeModal()} />
            </div>
            {getFileViewer && getFileViewer(fileExt, value)}
          </div>
        </div>
      )}
    </Transition>
  );
};
const CsvDisplay = ({ csvFileUrl }: { csvFileUrl: string }) => {
  const [csvData, setCsvData] = useState<any[]>([]);
  const [headerData, setHeaderData] = useState<string[]>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(csvFileUrl);
        const csvFile = await response.text();

        Papa.parse(csvFile, {
          header: true, // Treat the first row as headers
          skipEmptyLines: true, // Skip empty lines
          complete: (result) => {
            const tableRows = result.data;
            const headerColumns = result.meta.fields;
            setCsvData(tableRows);
            setHeaderData(headerColumns);
            // console.log("csv data ", result.data, result.meta.fields);
          },
        });
      } catch (error) {
        console.error("Error fetching or parsing CSV file:", error);
      }
    };

    fetchData();
  }, [csvFileUrl]);

  // const headers = Object.keys(csvData[0]);
  return (
    <div className="CsvTable">
      <table>
        <thead>
          {/* {csvData.length > 0 && (
            <tr>
              {Object.keys(csvData[0])?.map(header => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          )} */}
          {
            <tr>
              {headerData?.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          }
        </thead>
        <tbody>
          {csvData.map((row, index) => (
            <tr key={index}>
              {Object.values(row).map((cell, cellIndex) => (
                // <td key={cellIndex}>{cell ?? "-"}</td>
                <td key={cellIndex}>{row[headerData?.[cellIndex] as any] ?? "-"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
